/**
 * Google Identity Services helpers (Continue with Google).
 * Spec: no redirect — button returns id_token, frontend posts to author.google_login.
 */

function waitForGoogle(timeoutMs = 10000) {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) {
      resolve();
      return;
    }
    const started = Date.now();
    const timer = setInterval(() => {
      if (window.google?.accounts?.id) {
        clearInterval(timer);
        resolve();
      } else if (Date.now() - started > timeoutMs) {
        clearInterval(timer);
        reject(new Error("Google script failed to load. Check for an ad blocker."));
      }
    }, 50);
  });
}

function getContentWidth(el) {
  if (!el) return 0;
  const style = window.getComputedStyle(el);
  const pl = parseFloat(style.paddingLeft) || 0;
  const pr = parseFloat(style.paddingRight) || 0;
  return Math.max(0, el.clientWidth - pl - pr);
}

function nextFrame() {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

/**
 * Initialize GIS and render the official Continue with Google button into `container`.
 * Sizes the button to the container content box (with a small inset) so the outline
 * border is not clipped on desktop or mobile, and re-renders on resize.
 *
 * @param {HTMLElement} container
 * @param {(credential: string) => Promise<void>|void} onCredential
 * @param {(message: string) => void} onError
 * @returns {Promise<() => void>} cleanup (disconnects resize observer, clears slot)
 */
export async function renderGoogleButton(container, onCredential, onError) {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  if (!clientId) {
    onError("Google sign-in is not configured (missing client ID).");
    return () => {};
  }

  try {
    await waitForGoogle();
  } catch (e) {
    onError(e?.message || "Google script failed to load.");
    return () => {};
  }

  if (!container) return () => {};

  let cancelled = false;
  let lastWidth = 0;

  window.google.accounts.id.initialize({
    client_id: clientId,
    callback: async (response) => {
      try {
        if (!response?.credential) {
          onError("Google did not return a credential. Please try again.");
          return;
        }
        await onCredential(response.credential);
      } catch (e) {
        onError(e?.message || "Google sign-in failed");
      }
    },
    auto_select: false,
    cancel_on_tap_outside: true,
  });

  const paint = () => {
    if (cancelled || !container.isConnected) return;

    // Content-box width; 2px inset keeps the 1px outline inside overflow ancestors.
    const contentW = getContentWidth(container);
    if (contentW < 40) return;

    const width = Math.min(400, Math.max(120, Math.floor(contentW - 2)));
    if (container.childElementCount > 0 && Math.abs(width - lastWidth) < 2) return;

    lastWidth = width;
    container.innerHTML = "";

    window.google.accounts.id.renderButton(container, {
      theme: "outline",
      size: "large",
      text: "continue_with",
      shape: "rectangular",
      width,
    });
  };

  // Wait for layout so clientWidth is real (avoids rendering at 360 and clipping).
  await nextFrame();
  await nextFrame();
  if (!cancelled) paint();

  // Retry shortly in case fonts/layout settle after first paint.
  const retryTimer = window.setTimeout(() => {
    if (!cancelled) paint();
  }, 100);

  let resizeTimer = 0;
  const ro =
    typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(() => {
          window.clearTimeout(resizeTimer);
          resizeTimer = window.setTimeout(() => paint(), 50);
        })
      : null;
  ro?.observe(container);

  return () => {
    cancelled = true;
    window.clearTimeout(retryTimer);
    window.clearTimeout(resizeTimer);
    ro?.disconnect();
    try {
      container.innerHTML = "";
    } catch {
      /* ignore */
    }
  };
}

/** Call on sign-out so Google does not silently re-auth on next visit. */
export function disableGoogleAutoSelect() {
  try {
    window.google?.accounts?.id?.disableAutoSelect?.();
  } catch {
    // ignore
  }
}
